import { baseQuery } from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { TransactionRequest, TransactionDownloadRequest } from "shared/models/transaction.model";
import moment from "moment";
import { DisbursementDownloadRequest } from "shared/models/disbursements.model";
import { ChargeAutomationDownloadRequest } from "shared/models/chargeAuto.models";
import { Chargeback, ChargebackDownload, ChargebackRequest } from "shared/models/chargeback.model";
import { DataResponse } from "shared/models";
import { FileName } from "constants/common.constants";

export const payoutService = createApi({
  reducerPath: "payoutService",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getTransactions: builder.query<any, TransactionRequest>({
      query: ({ nameMerchant, skip_log, ...others }) => ({
        url: `/report/transactions`,
        method: "GET",
        params: others,
        headers: !skip_log
          ? {
            PageName: "Transaction",
          }
          : {},
      }),
    }),
    getDisbursementReport: builder.query<any, any>({
      query: (params) => ({
        url: `/report/payout`,
        method: "GET",
        params: {
          ...params,
          scenario: 'portal',
        },
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout"
            : "Payout Management",
        },
      }),
    }),
    getChargebackReport: builder.query<DataResponse<Chargeback[]>, ChargebackRequest>({
      query: (params) => ({
        url: `/report/chargebacks`,
        method: "GET",
        params,
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargeback",
        },
      }),
    }),
    getPayoutTransaction: builder.query<any, { id: string }>({
      query: ({ id }) => ({
        url: `/report/payout/${id}`,
        method: "GET",
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout"
            : "Payout Management",
        },
      }),
    }),
    createChargebackReport: builder.mutation<DataResponse<Chargeback>, Chargeback>({
      query: ({ id, ...others }) => ({
        url: `/report/chargebacks`,
        body: others,
        method: "POST",
        headers: {
          PageName: "Chargeback Management",
        },
      }),
    }),
    updateChargebackReport: builder.mutation<DataResponse<Chargeback>, Chargeback>({
      query: ({ id, ...others }) => ({
        url: `/report/chargebacks/${id}`,
        body: { id, ...others },
        method: "PUT",
        headers: {
          PageName: "Chargeback Management",
        },
      }),
    }),
    getDetailChargebackReport: builder.query<DataResponse<Chargeback>, { id: number }>({
      query: ({ id }) => ({
        url: `/report/chargebacks/${id}`,
        method: "GET",
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargeback",
        },
      }),
    }),
    getPayoutDetail: builder.query<any, { id: string }>({
      query: ({ id }) => ({
        url: `/report/payout/${id}/detail`,
        method: "GET",
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout"
            : "Payout Management",
        },
      }),
    }),
    updateDisbursementPayout: builder.mutation<any, any>({
      query: ({ id, ...others }) => ({
        url: `/report/payout/${id}/detail`,
        body: others,
        method: "PUT",
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout"
            : "Payout Management",
        },
      }),
    }),
    getDisbursementPayoutLogs: builder.query<{ id: string }, any>({
      query: (params) => ({
        url: `/report/disbursements/${params?.id}/logs`,
        method: "GET",
        params,
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout-Logs"
            : "Payout Management-Logs",
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          const data = rawData?.data?.messages;
          const newResponseData: any = [];
          data.forEach((item: any) => {
            let data: any = {};
            try {
              data = JSON.parse(item?.message?.message);
            } catch (e) {
              data = {};
            }

            const obj: any = {
              email: item?.message?.email,
              created_at: item?.message?.timestamp,
              affected: data?.affected,
            };
            if (data?.payable_amount !== undefined) {
              obj.payable_amount = data?.payable_amount;
              obj.payable_currency = data?.payable_currency;
            }
            if (data?.status) {
              obj.status = data?.status;
            }
            if (data?.description) {
              obj.description = data?.description;
            }
            if (data?.internal_note) {
              obj.internal_note = data?.internal_note;
            }

            newResponseData.push(obj);
          });
          newResponseData?.sort(
            (a: any, b: any) =>
              new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime()
          );
          return newResponseData;
        }
        return [];
      },
    }),
    getNewPayoutLogs: builder.query<{ id: string }, any>({
      query: (params) => ({
        url: `/report/payout/${params?.id}/logs`,
        method: "GET",
        params,
        headers: {
          PageName: String(window.location.href).includes("payout")
              ? "Payout-Logs"
              : "Payout Management-Logs",
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          const data = rawData?.data?.messages;
          const newResponseData: any = [];
          data.forEach((item: any) => {
            let data: any = {};
            try {
              data = JSON.parse(item?.message?.message);
            } catch (e) {
              data = {};
            }

            const obj: any = {
              email: item?.message?.email,
              created_at: item?.message?.timestamp
            };
            
            if (data?.paid_amount !== undefined) {
              obj.paid_amount = data?.paid_amount;
              obj.paid_currency = data?.paid_currency;
              obj.system_note = data?.system_note;
            }
            
              obj.system_note = data?.system_note || '';
            
            if (data?.status) {
              obj.status = data?.status;
            }

            newResponseData.push(obj);
          });
          newResponseData?.sort(
              (a: any, b: any) =>
                  new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime()
          );
          return newResponseData;
        }
        return [];
      },
    }),
    getChargebackLogs: builder.query<{ id: string }, any>({
      query: (params) => ({
        url: `/report/chargebacks/${params?.id}/logs`,
        method: "GET",
        params,
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargebacks",
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          const data = rawData?.data?.messages;
          const newResponseData: any = [];
          data.forEach((item: any, index: number) => {
            let logs: any = {};

            try {
              logs = JSON.parse(item?.message?.logs);
            } catch (e) {
              logs = {};
            }

            const obj: any = {
              email: item?.message?.email,
              ref_id: item?.message?.ref_id,
              created_at: item?.message?.timestamp,
              ...logs
            };

            newResponseData.push(obj);
          });
          newResponseData?.sort(
            (a: any, b: any) =>
              new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime()
          );
          return newResponseData;
        }
        return [];
      },
    }),
    getChargeAutomation: builder.query<any, any>({
      query: (params) => ({
        url: `/report/automationCharges`,
        method: "GET",
        params,
        headers: {
          PageName: "Charge Automation",
        },
      }),
    }),
    getDetailChargeAutomation: builder.query<any, any>({
      query: (params) => ({
        url: `/report/automationCharges/${params?.id}/logs`,
        method: "GET",
        headers: {
          PageName: "Charge Automation",
        },
        params,
      }),
    }),
    downloadChargeAutomation: builder.query<any, ChargeAutomationDownloadRequest>({
      query: ({ nameMerchant, ...others }) => ({
        url: `/report/automationCharges/download`,
        method: "GET",
        params: others,
        headers: {
          PageName: "Charge Automation",
        },
      }),
    }),
    downloadTransactions: builder.query<any, TransactionDownloadRequest>({
      query: ({ nameMerchant, skip_log, ...others }) => ({
        url: `/report/transactions/download`,
        method: "GET",
        params: others,
        headers: !skip_log
          ? {
            PageName: "Transaction",
          }
          : {},
      }),
    }),
    downloadDisbursements: builder.query<any, DisbursementDownloadRequest>({
      query: ({ nameMerchant, ...others }) => ({
        url: `/report/payout/download`,
        method: "GET",
        params: others,
        headers: {
          PageName: "Payout",
        },
      }),
    }),
    downloadChargebacks: builder.query<any, ChargebackDownload>({
      query: ({ nameMerchant, ...others }) => ({
        url: `report/chargebacks/download`,
        method: "GET",
        params: others,
        headers: {
          PageName: String(window.location.href).includes("admin/chargeback-management")
            ? "Chargeback Management"
            : "Chargeback",
        },
      }),
    }),
    moveTransaction: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: `/report/payout/moveTransaction`,
        body,
        method: "POST",
        headers: {
          PageName: "Payout Management",
        },
      }),
    }),
    reSyncTransaction: builder.query<any, { transaction_sync_id: number }>({
      query: ({ transaction_sync_id }) => ({
        url: `/report/payout/transaction/reSyncTransaction/${transaction_sync_id}`,
        method: "GET",
        headers: {
          PageName: String(window.location.href).includes("payout")
            ? "Payout"
            : "Payout Management",
        },
      }),
    }),
  }),
});

export const {
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
  useLazyDownloadTransactionsQuery,

  useGetDisbursementReportQuery,
  useLazyGetDisbursementReportQuery,
  useLazyGetPayoutTransactionQuery,
  useLazyGetPayoutDetailQuery,
  useLazyDownloadDisbursementsQuery,
  useLazyGetDisbursementPayoutLogsQuery,
  useUpdateDisbursementPayoutMutation,

  useGetChargeAutomationQuery,
  useLazyGetChargeAutomationQuery,
  useLazyGetDetailChargeAutomationQuery,
  useLazyDownloadChargeAutomationQuery,

  useGetChargebackReportQuery,
  useGetDetailChargebackReportQuery,
  useLazyGetDetailChargebackReportQuery,
  useLazyGetChargebackReportQuery,
  useCreateChargebackReportMutation,
  useUpdateChargebackReportMutation,
  useLazyGetChargebackLogsQuery,
  useLazyDownloadChargebacksQuery,
  useLazyGetNewPayoutLogsQuery,

  useMoveTransactionMutation,
  useLazyReSyncTransactionQuery,
} = payoutService;